import MobileService from './MobileService';

class OfflineService {
  constructor() {
    this.isOnline = true;
    this.offlineData = {
      events: [],
      currentDate: null,
      settings: {},
      tithiData: null,
      dinaNitya: null
    };
    this.init();
  }

  init() {
    // Listen for network changes
    MobileService.setupNetworkListener((status) => {
      this.isOnline = status.connected;
      console.log('Network status changed:', status);
      
      if (this.isOnline) {
        this.syncOfflineData();
      }
      
      // Dispatch custom event for components to listen
      window.dispatchEvent(new CustomEvent('network-change', {
        detail: { isOnline: this.isOnline, ...status }
      }));
    });

    // Load cached data on init
    this.loadCachedData();
  }

  // Cache Management
  async cacheData(key, data) {
    try {
      await MobileService.setPreference(`cache_${key}`, {
        data,
        timestamp: Date.now()
      });
      this.offlineData[key] = data;
    } catch (error) {
      console.log('Cache data error:', error);
    }
  }

  async getCachedData(key, maxAge = 24 * 60 * 60 * 1000) { // 24 hours default
    try {
      const cached = await MobileService.getPreference(`cache_${key}`);
      
      if (cached && cached.data) {
        const age = Date.now() - cached.timestamp;
        
        if (age < maxAge) {
          return cached.data;
        } else {
          // Cache expired, remove it
          await this.removeCachedData(key);
        }
      }
      
      return null;
    } catch (error) {
      console.log('Get cached data error:', error);
      return null;
    }
  }

  async removeCachedData(key) {
    try {
      await MobileService.removePreference(`cache_${key}`);
      if (this.offlineData[key]) {
        this.offlineData[key] = null;
      }
    } catch (error) {
      console.log('Remove cached data error:', error);
    }
  }

  // Load all cached data
  async loadCachedData() {
    const keys = ['events', 'currentDate', 'settings', 'tithiData', 'dinaNitya'];
    
    for (const key of keys) {
      const cachedData = await this.getCachedData(key);
      if (cachedData) {
        this.offlineData[key] = cachedData;
      }
    }
  }

  // Devimana Calendar Offline Data
  async cacheCurrentDate(dateData) {
    await this.cacheData('currentDate', dateData);
  }

  async getCachedCurrentDate() {
    return await this.getCachedData('currentDate', 2 * 60 * 60 * 1000); // 2 hours for current date
  }

  // Events Offline Data
  async cacheEvents(events) {
    await this.cacheData('events', events);
  }

  async getCachedEvents() {
    return await this.getCachedData('events') || [];
  }

  async addOfflineEvent(event) {
    const events = await this.getCachedEvents();
    const offlineEvent = {
      ...event,
      id: `offline_${Date.now()}`,
      isOffline: true,
      createdOffline: true
    };
    
    events.push(offlineEvent);
    await this.cacheEvents(events);
    
    return offlineEvent;
  }

  // Settings Offline Data
  async cacheSettings(settings) {
    await this.cacheData('settings', settings);
  }

  async getCachedSettings() {
    return await this.getCachedData('settings') || {
      kulaType: 'shri_kula',
      tantraType: 'saubhagya',
      tithiMethod: 'visuddha_siddhanta',
      selectedCity: 'Delhi'
    };
  }

  // Tithi Data Offline Cache
  async cacheTithiData(tithiData) {
    await this.cacheData('tithiData', tithiData);
  }

  async getCachedTithiData() {
    return await this.getCachedData('tithiData', 6 * 60 * 60 * 1000); // 6 hours for tithi
  }

  // Dina Nitya Offline Cache
  async cacheDinaNitya(dinaNityaData) {
    await this.cacheData('dinaNitya', dinaNityaData);
  }

  async getCachedDinaNitya() {
    return await this.getCachedData('dinaNitya', 24 * 60 * 60 * 1000); // 24 hours
  }

  // Sync offline data when coming back online
  async syncOfflineData() {
    console.log('Syncing offline data...');
    
    try {
      // Sync offline events
      await this.syncOfflineEvents();
      
      // Refresh cached data when online
      await this.refreshOnlineData();
      
      console.log('Offline data sync completed');
    } catch (error) {
      console.log('Sync offline data error:', error);
    }
  }

  async syncOfflineEvents() {
    const events = await this.getCachedEvents();
    const offlineEvents = events.filter(event => event.isOffline);
    
    if (offlineEvents.length > 0) {
      console.log(`Syncing ${offlineEvents.length} offline events`);
      
      // Here you would typically send these to the server
      // For now, we'll just mark them as synced
      const syncedEvents = events.map(event => {
        if (event.isOffline) {
          return { ...event, isOffline: false, synced: true };
        }
        return event;
      });
      
      await this.cacheEvents(syncedEvents);
    }
  }

  async refreshOnlineData() {
    // Dispatch event to trigger data refresh in components
    window.dispatchEvent(new CustomEvent('refresh-online-data'));
  }

  // Offline Status
  getOfflineStatus() {
    return {
      isOnline: this.isOnline,
      hasOfflineEvents: this.offlineData.events?.some(e => e.isOffline) || false,
      cachedDataKeys: Object.keys(this.offlineData).filter(key => this.offlineData[key] !== null)
    };
  }

  // Clear all offline data
  async clearOfflineData() {
    const keys = ['events', 'currentDate', 'settings', 'tithiData', 'dinaNitya'];
    
    for (const key of keys) {
      await this.removeCachedData(key);
    }
    
    this.offlineData = {
      events: [],
      currentDate: null,
      settings: {},
      tithiData: null,
      dinaNitya: null
    };
    
    console.log('All offline data cleared');
  }

  // Get offline-ready data for components
  async getOfflineReadyData(key) {
    if (this.isOnline) {
      // If online, return fresh data (components should handle API calls)
      return null;
    } else {
      // If offline, return cached data
      return await this.getCachedData(key);
    }
  }

  // Export cached data for debugging
  async exportCachedData() {
    const data = {};
    const keys = ['events', 'currentDate', 'settings', 'tithiData', 'dinaNitya'];
    
    for (const key of keys) {
      data[key] = await this.getCachedData(key);
    }
    
    return data;
  }
}

export default new OfflineService();